import React, { useState, useEffect, useContext, useRef, useImperativeHandle, forwardRef } from "react";
import {
  Box,
  Button,
  Typography,
  Autocomplete,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InventoryIcon from "@mui/icons-material/Inventory";
import { ErpContext } from "../../context/ErpContext";
import CrearLoteModal from "./CrearLoteModal";
import VerLotesModal from "./VerLotesModal";
import TablaLoteUbicaciones from "./TablaLoteUbicaciones";

const GestionLoteTab = forwardRef(({ itemId, ubicaciones }, ref) => {
  const {
    getLotesByItem,
    getLoteItemUbicacionStock,
  } = useContext(ErpContext);

  const [lotes, setLotes] = useState([]);
  const [loteSeleccionado, setLoteSeleccionado] = useState(null);
  const [stockPorUbicacion, setStockPorUbicacion] = useState([]);
  const [stockTotal, setStockTotal] = useState(0);
  const [openCrearLote, setOpenCrearLote] = useState(false);
  const [openVerLotes, setOpenVerLotes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ref para acceder a la tabla de ubicaciones
  const tablaLoteUbicacionesRef = useRef();

  // Cargar lotes del item al montar el componente
  useEffect(() => {
    if (itemId) {
      cargarLotes();
    }
  }, [itemId]);

  // Cargar stock cuando cambia el lote seleccionado
  useEffect(() => {
    if (loteSeleccionado && itemId) {
      cargarStockLote();
    } else {
      setStockPorUbicacion([]);
    }
  }, [loteSeleccionado]);

  // Calcular stock total cuando cambian los lotes
  useEffect(() => {
    calcularStockTotal();
  }, [lotes]);

  const cargarLotes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const lotesData = await getLotesByItem(itemId);
      setLotes(lotesData || []);
    } catch (err) {
      console.error("Error al cargar lotes:", err);
      setError("Error al cargar los lotes del item");
    } finally {
      setIsLoading(false);
    }
  };

  const cargarStockLote = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const stockData = await getLoteItemUbicacionStock(loteSeleccionado.id);
      setStockPorUbicacion(stockData || []);
    } catch (err) {
      console.error("Error al cargar stock de lote:", err);
      setError("Error al cargar el stock del lote");
    } finally {
      setIsLoading(false);
    }
  };

  const calcularStockTotal = async () => {
    if (!lotes || lotes.length === 0) {
      setStockTotal(0);
      return;
    }

    try {
      let total = 0;
      
      // Para cada lote, obtener su stock y sumarlo
      for (const lote of lotes) {
        try {
          const stockData = await getLoteItemUbicacionStock(lote.id);
          if (stockData && Array.isArray(stockData)) {
            const stockLote = stockData.reduce((sum, ubicacion) => {
              return sum + (parseFloat(ubicacion.stock) || 0);
            }, 0);
            total += stockLote;
          }
        } catch (err) {
          console.warn(`Error al obtener stock del lote ${lote.id}:`, err);
        }
      }
      
      setStockTotal(total);
    } catch (err) {
      console.error("Error al calcular stock total:", err);
      setStockTotal(0);
    }
  };

  const handleLoteCreado = () => {
    setOpenCrearLote(false);
    cargarLotes();
    // El stock total se recalculará automáticamente por el useEffect
  };

  const handleSeleccionLote = (event, newValue) => {
    setLoteSeleccionado(newValue);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return "Sin fecha";
    return new Date(fecha).toLocaleDateString("es-AR");
  };

  const handleStockActualizado = () => {
    cargarStockLote();
    // Recalcular stock total después de actualizar stock de un lote
    calcularStockTotal();
  };

  // 🆕 Función para guardar cambios pendientes de lotes (expuesta al modal padre)
  const guardarCambiosPendientesLotes = async () => {
    if (!tablaLoteUbicacionesRef.current) {
      console.log("ℹ️  No hay tabla de lotes para guardar cambios");
      return { exitosos: 0, fallidos: 0, errores: [] };
    }

    try {
      const resultado = await tablaLoteUbicacionesRef.current.guardarTodosLosCambiosPendientes();
      
      // Recalcular stock total después de guardar
      if (resultado.exitosos > 0) {
        await calcularStockTotal();
      }
      
      return resultado;
    } catch (err) {
      console.error("Error al guardar cambios pendientes de lotes:", err);
      return { 
        exitosos: 0, 
        fallidos: 1, 
        errores: [err.message || "Error desconocido"] 
      };
    }
  };

  // Exponer funciones al modal padre mediante ref
  useImperativeHandle(ref, () => ({
    guardarCambiosPendientesLotes
  }));

  return (
    <Box sx={{ p: 3 }}>
      {/* Título */}
      <Typography
        variant="h6"
        sx={{ mb: 3, color: "black", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <InventoryIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
          Gestión de Lotes
        </Box>
        <Typography
          variant="body2"
          sx={{ 
            color: "rgb(244, 147, 14)", 
            fontWeight: 600,
            backgroundColor: "rgba(244, 147, 14, 0.1)",
            px: 2,
            py: 0.5,
            borderRadius: 1,
            border: "1px solid rgba(244, 147, 14, 0.3)"
          }}
        >
          Stock total: {stockTotal.toLocaleString('es-AR')}
        </Typography>
      </Typography>

      {/* Botones superiores */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCrearLote(true)}
          sx={{
            backgroundColor: "rgb(244, 147, 14)",
            "&:hover": {
              backgroundColor: "rgb(220, 130, 10)",
            },
          }}
        >
          Crear Lote
        </Button>
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={() => setOpenVerLotes(true)}
          disabled={lotes.length === 0}
          sx={{
            borderColor: "rgb(244, 147, 14)",
            color: "rgb(244, 147, 14)",
            "&:hover": {
              borderColor: "rgb(220, 130, 10)",
              backgroundColor: "rgba(244, 147, 14, 0.04)",
            },
          }}
        >
          Ver Lotes ({lotes.length})
        </Button>
      </Box>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Mensaje si no hay lotes */}
      {!isLoading && lotes.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No hay lotes creados para este item. Haz clic en "Crear Lote" para comenzar.
        </Alert>
      )}

      {/* Dropdown de selección de lote */}
      {lotes.length > 0 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Seleccionar Lote
            </Typography>
            <Autocomplete
              options={lotes}
              getOptionLabel={(option) =>
                `${option.numeroLote} - Fab: ${formatFecha(
                  option.fechaFabricacion
                )} | Venc: ${formatFecha(option.fechaVencimiento)}`
              }
              value={loteSeleccionado}
              onChange={handleSeleccionLote}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar lote por número o fecha"
                  variant="outlined"
                  fullWidth
                  placeholder="Selecciona un lote..."
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {option.numeroLote}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fabricación: {formatFecha(option.fechaFabricacion)} | 
                      Vencimiento: {formatFecha(option.fechaVencimiento)}
                    </Typography>
                    {option.observaciones && (
                      <Typography variant="caption" color="text.secondary">
                        {option.observaciones}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No se encontraron lotes"
              loadingText="Cargando..."
            />
          </CardContent>
        </Card>
      )}

      {/* Loading spinner */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress sx={{ color: "rgb(244, 147, 14)" }} />
        </Box>
      )}

      {/* Tabla de stock por ubicación */}
      {loteSeleccionado && !isLoading && (
        <TablaLoteUbicaciones
          ref={tablaLoteUbicacionesRef}
          loteId={loteSeleccionado.id}
          itemId={itemId}
          ubicaciones={ubicaciones}
          stockPorUbicacion={stockPorUbicacion}
          onStockActualizado={handleStockActualizado}
        />
      )}

      {/* Modales */}
      <CrearLoteModal
        open={openCrearLote}
        onClose={() => setOpenCrearLote(false)}
        onLoteCreado={handleLoteCreado}
        itemId={itemId}
        ubicaciones={ubicaciones}
      />

      <VerLotesModal
        open={openVerLotes}
        onClose={() => setOpenVerLotes(false)}
        lotes={lotes}
        itemId={itemId}
        onUpdate={() => {
          cargarLotes();
          // El stock total se recalculará automáticamente por el useEffect
        }}
      />
    </Box>
  );
});

export default GestionLoteTab;


